// URL sharing utilities for Baby Food Tracker
import { foodData } from './foodData.js';

// Create a fixed order of all foods for consistent encoding
function getAllFoodsInOrder() {
    const allFoods = [];
    // Maintain the order: Fruits, Veggies, Grains, Protein, Allergens
    const categoryOrder = ['Fruits', 'Veggies', 'Grains', 'Protein', 'Allergens'];

    categoryOrder.forEach(category => {
        const categoryFoods = foodData.filter(food => food.type === category);
        allFoods.push(...categoryFoods);
    });

    return allFoods;
}

// Generate food key (same as in ui.js)
function getFoodKey(food) {
    return `${food.en}_${food.zh}`;
}

// Convert progress object to binary string
export function progressToBinaryString(progress) {
    const allFoods = getAllFoodsInOrder();
    let binaryString = '';

    allFoods.forEach(food => {
        const key = getFoodKey(food);
        // Convert boolean to '1' or '0'
        binaryString += progress[key] === true ? '1' : '0';
    });

    return binaryString;
}

// Convert binary string to progress object
export function binaryStringToProgress(binaryString) {
    const allFoods = getAllFoodsInOrder();
    const progress = {};

    // Validate binary string length
    if (binaryString.length !== allFoods.length) {
        throw new Error(`Invalid binary string length. Expected ${allFoods.length}, got ${binaryString.length}`);
    }

    allFoods.forEach((food, index) => {
        const key = getFoodKey(food);
        progress[key] = binaryString[index] === '1';
    });

    return progress;
}

// Convert binary string to base64 (URL-safe)
export function binaryStringToBase64(binaryString) {
    // Pad binary string to multiple of 8 if needed
    const paddedLength = Math.ceil(binaryString.length / 8) * 8;
    const paddedBinary = binaryString.padEnd(paddedLength, '0');

    // Convert binary to bytes
    const bytes = [];
    for (let i = 0; i < paddedBinary.length; i += 8) {
        const byte = paddedBinary.slice(i, i + 8);
        bytes.push(parseInt(byte, 2));
    }

    // Convert bytes to base64
    const uint8Array = new Uint8Array(bytes);
    const binaryStr = String.fromCharCode.apply(null, uint8Array);

    // Use URL-safe base64 encoding
    return btoa(binaryStr)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

// Convert base64 to binary string
export function base64ToBinaryString(base64String) {
    try {
        // Convert URL-safe base64 back to regular base64
        let base64 = base64String
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        // Add padding if needed
        while (base64.length % 4) {
            base64 += '=';
        }

        // Decode base64 to binary string
        const binaryStr = atob(base64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
        }

        // Convert bytes to binary string
        let binaryString = '';
        bytes.forEach(byte => {
            binaryString += byte.toString(2).padStart(8, '0');
        });

        // Trim to exact length needed (100 foods)
        const allFoods = getAllFoodsInOrder();
        return binaryString.slice(0, allFoods.length);
    } catch (error) {
        throw new Error('Invalid base64 string: ' + error.message);
    }
}

// Generate shareable URL
export function generateShareableURL(progress) {
    const binaryString = progressToBinaryString(progress);
    const base64String = binaryStringToBase64(binaryString);

    const url = new URL(window.location.href);
    url.searchParams.set('data', base64String);

    return url.toString();
}

// Load progress from URL parameter
export function loadProgressFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get('data');

    if (!dataParam) {
        return null;
    }

    try {
        const binaryString = base64ToBinaryString(dataParam);
        const progress = binaryStringToProgress(binaryString);
        return progress;
    } catch (error) {
        console.error('Failed to load progress from URL:', error);
        return null;
    }
}

// Remove data parameter from URL without page reload
export function clearURLParameter() {
    const url = new URL(window.location.href);
    url.searchParams.delete('data');
    window.history.replaceState({}, document.title, url.toString());
}
